import React, { Fragment, useEffect } from "react";
import { Menu, Transition } from "@headlessui/react";
import { IconBuildingWarehouse, IconChevronDown, IconCheck, IconSettings } from "@tabler/icons-react";
import { useBranches } from "../controllers/branch.controller";
import { getUserDetailsInLocalStorage } from "../helpers/UserDetails";
import { useTheme } from "../contexts/ThemeContext";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import clsx from "clsx";

export default function BranchSwitcher() {
  const { branches, isLoading } = useBranches();
  const user = getUserDetailsInLocalStorage();
  const { theme } = useTheme();
  const navigate = useNavigate();

  // If user is not logged in or is superadmin, don't show branch switcher
  if (!user || user.role === "superadmin") {
    return null;
  }

  // Active branch ID from localStorage
  const savedBranchId = localStorage.getItem("active_branch_id");

  // Determine active branch:
  // 1. If staff with fixed branch_id, they are always that branch
  // 2. If admin, check savedBranchId in localStorage, otherwise fallback to main branch or first branch
  let activeBranch = null;

  if (user.branch_id) {
    activeBranch = branches.find((b) => Number(b.id) === Number(user.branch_id)) || {
      id: user.branch_id,
      branch_name: user.branch_name || "Assigned Branch",
      branch_code: user.branch_code || "BRANCH",
    };
  } else if (branches && branches.length > 0) {
    if (savedBranchId) {
      activeBranch = branches.find((b) => Number(b.id) === Number(savedBranchId));
    }
    if (!activeBranch) {
      activeBranch = branches.find((b) => b.is_main === 1) || branches[0];
      if (activeBranch) {
        localStorage.setItem("active_branch_id", activeBranch.id);
        localStorage.setItem("active_branch_name", activeBranch.branch_name);
      }
    }
  }

  // Sync to localStorage on branch list load if missing
  useEffect(() => {
    if (!user.branch_id && branches && branches.length > 0 && !localStorage.getItem("active_branch_id")) {
      const main = branches.find((b) => b.is_main === 1) || branches[0];
      if (main) {
        localStorage.setItem("active_branch_id", main.id);
        localStorage.setItem("active_branch_name", main.branch_name);
      }
    }
  }, [branches, user.branch_id]);

  const handleSelectBranch = (branch) => {
    if (Number(branch.id) === Number(activeBranch?.id)) return;
    localStorage.setItem("active_branch_id", branch.id);
    localStorage.setItem("active_branch_name", branch.branch_name);
    toast.success(`Switched to ${branch.branch_name}`);
    window.location.reload();
  };

  // Staff user: read-only badge
  if (user.branch_id) {
    return (
      <div
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm border bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800"
        aria-label={`Staff branch: ${activeBranch?.branch_name || "Assigned Branch"}`}
      >
        <IconBuildingWarehouse size={15} className="shrink-0 text-amber-600 dark:text-amber-400" />
        <span className="truncate max-w-[120px]">{activeBranch?.branch_name || "Assigned Branch"}</span>
      </div>
    );
  }

  // Admin user with only 1 branch: static indicator
  if (!branches || branches.length <= 1) {
    return (
      <div
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm border bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800"
        aria-label="Current Branch"
      >
        <IconBuildingWarehouse size={15} className="shrink-0 text-emerald-600 dark:text-emerald-400" />
        <span className="truncate max-w-[120px]">{activeBranch?.branch_name || "Main Branch"}</span>
      </div>
    );
  }

  // Admin user with multiple branches: interactive switcher dropdown
  return (
    <Menu as="div" className="relative inline-block text-left z-40">
      <div>
        <Menu.Button
          className={clsx(
            "flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm border transition hover:opacity-90 active:scale-95",
            theme === "black"
              ? "bg-neutral-800 text-neutral-100 border-neutral-700 hover:bg-neutral-700"
              : "bg-emerald-50 text-emerald-900 border-emerald-300 hover:bg-emerald-100"
          )}
          aria-label="Switch Active Branch"
        >
          <IconBuildingWarehouse size={15} className="shrink-0 text-emerald-600 dark:text-emerald-400" />
          <span className="truncate max-w-[130px]">
            {activeBranch ? activeBranch.branch_name : "Select Branch"}
          </span>
          <IconChevronDown size={14} className="shrink-0 opacity-70" />
        </Menu.Button>
      </div>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items
          className={clsx(
            "absolute right-0 mt-2 w-64 origin-top-right divide-y divide-gray-100 dark:divide-neutral-700 rounded-2xl shadow-xl ring-1 ring-black/5 focus:outline-none p-1.5 z-50",
            theme === "black" ? "bg-neutral-900 border border-neutral-800 text-white" : "bg-white border border-gray-200 text-gray-800"
          )}
        >
          <div className="px-3 py-2">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">
              Tenant Branches
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Select branch to manage orders & stock
            </p>
          </div>

          <div className="py-1 max-h-60 overflow-y-auto">
            {branches.map((b) => {
              const isSelected = Number(b.id) === Number(activeBranch?.id);
              return (
                <Menu.Item key={b.id}>
                  {({ active }) => (
                    <button
                      onClick={() => handleSelectBranch(b)}
                      className={clsx(
                        "w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-medium transition text-left",
                        isSelected
                          ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-semibold"
                          : active
                          ? theme === "black"
                            ? "bg-neutral-800 text-white"
                            : "bg-gray-100 text-gray-900"
                          : theme === "black"
                          ? "text-gray-300"
                          : "text-gray-700"
                      )}
                    >
                      <div className="flex flex-col truncate pr-2">
                        <span className="truncate">{b.branch_name}</span>
                        <span className="text-[10px] text-gray-400 font-normal">
                          {b.branch_code} {b.is_main ? "• Main" : ""}
                        </span>
                      </div>
                      {isSelected && (
                        <IconCheck size={16} className="text-emerald-500 shrink-0" />
                      )}
                    </button>
                  )}
                </Menu.Item>
              );
            })}
          </div>

          <div className="pt-1">
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={() => navigate("/dashboard/settings/branches")}
                  className={clsx(
                    "w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium transition",
                    active
                      ? theme === "black"
                        ? "bg-neutral-800 text-white"
                        : "bg-gray-100 text-gray-900"
                      : "text-gray-500 dark:text-gray-400"
                  )}
                >
                  <IconSettings size={14} className="shrink-0" />
                  <span>Manage Branches</span>
                </button>
              )}
            </Menu.Item>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
